import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthHero from '../components/AuthHero';

interface PasswordData {
    password: string;
    confirmPassword: string;
}

interface FieldErrors {
    password?: string;
    confirmPassword?: string;
}

const CreatePassword: React.FC = () => {
    const navigate = useNavigate();
    const [passwordData, setPasswordData] = useState<PasswordData>({
        password: '',
        confirmPassword: '',
    });
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleInputChange = (field: keyof PasswordData, value: string) => {
        setPasswordData(prev => ({
            ...prev,
            [field]: value,
        }));

        // Clear field error when user starts typing
        if (fieldErrors[field]) {
            setFieldErrors(prev => ({
                ...prev,
                [field]: undefined,
            }));
        }
    };

    const validateForm = (): boolean => {
        const errors: FieldErrors = {};

        if (!passwordData.password) {
            errors.password = 'Password is required';
        } else if (passwordData.password.length < 8) {
            errors.password = 'Password must be at least 8 characters';
        } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(passwordData.password)) {
            errors.password = 'Password must contain uppercase, lowercase, and number';
        }

        if (!passwordData.confirmPassword) {
            errors.confirmPassword = 'Please confirm your password';
        } else if (passwordData.password !== passwordData.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);

        try {
            // TODO: Submit password to backend

            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Navigate to success page or login
            navigate('/auth/login');
        } catch (error) {
            console.error('Password creation failed:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F8F9FB] flex">
            {/* Left Side - Form */}
            <div className="w-full lg:flex-1 flex items-center justify-center p-4 sm:p-8">
                <div className="w-full max-w-md">
                    {/* Logo */}
                    <div className="mb-8">
                        <img
                            src="/branding/logo_blue.svg"
                            alt="Hush"
                            onClick={() => navigate('/')}
                            style={{
                                width: '120px',
                                height: '55px',
                                objectFit: 'contain',
                                cursor: 'pointer'
                            }}
                        />
                    </div>

                    {/* Title */}
                    <h1
                        style={{
                            fontFamily: 'Manrope',
                            fontWeight: 700,
                            fontSize: '32px',
                            color: '#015FA6',
                            marginBottom: '8px'
                        }}
                    >
                        Create new password
                    </h1>

                    {/* Subtitle */}
                    <p
                        style={{
                            fontFamily: 'SF Pro, Manrope',
                            fontSize: '14px',
                            color: '#015FA6',
                            marginBottom: '32px'
                        }}
                    >
                        Your new password must be different from previously used passwords
                    </p>

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        {/* Password Field */}
                        <div style={{ marginBottom: '20px' }}>
                            <label
                                style={{
                                    fontFamily: 'Manrope',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: '#015FA6',
                                    display: 'block',
                                    marginBottom: '8px'
                                }}
                            >
                                Password
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={passwordData.password}
                                    onChange={(e) => handleInputChange('password', e.target.value)}
                                    placeholder="••••••"
                                    style={{
                                        width: '100%',
                                        padding: '12px 40px 12px 16px',
                                        fontFamily: 'Manrope',
                                        fontSize: '14px',
                                        color: 'rgba(1, 95, 166, 0.5)',
                                        background: '#F7FAFC',
                                        border: fieldErrors.password ? '1px solid #EF4444' : '1px solid #CBD5E0',
                                        borderRadius: '8px',
                                        boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                        outline: 'none',
                                        transition: 'border-color 0.2s ease',
                                    }}
                                    onFocus={(e) => {
                                        if (!fieldErrors.password) {
                                            e.target.style.borderColor = '#015FA6';
                                            e.target.style.color = '#015FA6';
                                        }
                                    }}
                                    onBlur={(e) => {
                                        if (!fieldErrors.password) {
                                            e.target.style.borderColor = '#CBD5E0';
                                            if (!e.target.value) {
                                                e.target.style.color = 'rgba(1, 95, 166, 0.5)';
                                            }
                                        }
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{
                                        position: 'absolute',
                                        right: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: '#6B7280',
                                        padding: '4px'
                                    }}
                                >
                                    {showPassword ? (
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M1 1l22 22" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    ) : (
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                                            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth={1.5}/>
                                        </svg>
                                    )}
                                </button>
                            </div>
                            {fieldErrors.password && (
                                <p
                                    style={{
                                        fontFamily: 'Manrope',
                                        fontSize: '12px',
                                        color: '#EF4444',
                                        marginTop: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                        <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    {fieldErrors.password}
                                </p>
                            )}
                            <p
                                style={{
                                    fontFamily: 'Manrope',
                                    fontSize: '12px',
                                    color: 'rgba(1, 95, 166, 0.6)',
                                    marginTop: '4px',
                                }}
                            >
                                Must be at least 8 characters with uppercase, lowercase, and number
                            </p>
                        </div>

                        {/* Confirm Password Field */}
                        <div style={{ marginBottom: '24px' }}>
                            <label
                                style={{
                                    fontFamily: 'Manrope',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: '#015FA6',
                                    display: 'block',
                                    marginBottom: '8px'
                                }}
                            >
                                Confirm password
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={passwordData.confirmPassword}
                                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                                    placeholder="••••••"
                                    style={{
                                        width: '100%',
                                        padding: '12px 40px 12px 16px',
                                        fontFamily: 'Manrope',
                                        fontSize: '14px',
                                        color: 'rgba(1, 95, 166, 0.5)',
                                        background: '#F7FAFC',
                                        border: fieldErrors.confirmPassword ? '1px solid #EF4444' : '1px solid #CBD5E0',
                                        borderRadius: '8px',
                                        boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                        outline: 'none',
                                        transition: 'border-color 0.2s ease',
                                    }}
                                    onFocus={(e) => {
                                        if (!fieldErrors.confirmPassword) {
                                            e.target.style.borderColor = '#015FA6';
                                            e.target.style.color = '#015FA6';
                                        }
                                    }}
                                    onBlur={(e) => {
                                        if (!fieldErrors.confirmPassword) {
                                            e.target.style.borderColor = '#CBD5E0';
                                            if (!e.target.value) {
                                                e.target.style.color = 'rgba(1, 95, 166, 0.5)';
                                            }
                                        }
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    style={{
                                        position: 'absolute',
                                        right: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: '#6B7280',
                                        padding: '4px'
                                    }}
                                >
                                    {showConfirmPassword ? (
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M1 1l22 22" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    ) : (
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                                            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth={1.5}/>
                                        </svg>
                                    )}
                                </button>
                            </div>
                            {fieldErrors.confirmPassword && (
                                <p
                                    style={{
                                        fontFamily: 'Manrope',
                                        fontSize: '12px',
                                        color: '#EF4444',
                                        marginTop: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                        <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    {fieldErrors.confirmPassword}
                                </p>
                            )}
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            style={{
                                width: '100%',
                                padding: '14px',
                                fontFamily: 'Manrope',
                                fontWeight: 600,
                                fontSize: '16px',
                                color: '#F7FAFC',
                                background: isLoading ? '#9CA3AF' : '#015FA6',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                transition: 'background-color 0.2s ease',
                                marginBottom: '16px'
                            }}
                            onMouseEnter={(e) => {
                                if (!isLoading) {
                                    e.currentTarget.style.background = '#014880';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isLoading) {
                                    e.currentTarget.style.background = '#015FA6';
                                }
                            }}
                        >
                            {isLoading ? 'Updating password...' : 'Reset password'}
                        </button>

                        {/* Back to Sign In */}
                        <div style={{ textAlign: 'center' }}>
                            <button
                                type="button"
                                onClick={() => navigate('/auth/login')}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#015FA6',
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    fontFamily: 'SF Pro, Manrope',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '4px',
                                    margin: '0 auto'
                                }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <path d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                Back to Sign in
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Right Side - Hero */}
            <div className="hidden lg:flex lg:flex-1" style={{ background: '#015FA6' }}>
                <AuthHero />
            </div>
        </div>
    );
};

export default CreatePassword;
